// ════════════════════════════════════════════════════════════════════════════
//  ManDana — foundation module (Liquid Glass design system)
//  ----------------------------------------------------------------------------
//  The base layer every other script builds on. Loaded first; everything it
//  exposes is published to `window` at the bottom for the sibling babel scripts
//  (md-sunarc / md-today / md-app) to pull in.
//
//  Sections below:
//    DATA            virtual "now", prayer times, per-weekday events, calendar +
//                    Hijri model, and mdDayItems() (the merged, ordered day list)
//    HELPERS         colour math (hex/rgba/hsl, vibrancy), time formatting,
//                    next/prev prayer lookup
//    THEME           light/dark switch + ink()/surf()/onAcc() colour tokens and
//                    the dark-glass tunables
//    ICONS           the stroked SVG icon set (MDI)
//    GLASS PRIMITIVES <Glass> frosted surface
// ════════════════════════════════════════════════════════════════════════════

/* ───────────────────────── DATA ───────────────────────── */
/* ── the clock ─────────────────────────────────────────────────────────────
   Was a frozen virtual "now" (17:08, chosen so the live now-line sat on an
   active prayer card while the design was being drawn). It is the real clock
   now: read fresh on every call rather than captured once, so an app left open
   crosses midnight correctly instead of counting past 24:00.

   The design-time scrubber still works — md-app.jsx prefers the `nowMin` tweak
   when it is set, and falls back to these. */
function mdNowSec(){ const d = new Date(); return d.getHours()*3600 + d.getMinutes()*60 + d.getSeconds(); }
function mdNowMin(){ return mdNowSec() / 60; }

/* ── prayer times ───────────────────────────────────────────────────────────
   Was five hardcoded strings for an unnamed place. Computed now, per day, from
   real coordinates via adhan — the same library family the retired Kotlin engine
   used, so the algorithms are the ones this product was designed around.

   Only the names are static; every time comes from the sun. */
const MD_PRAYER_META = [
  { id:'fajr',    name:'Fajr',    ar:'الفجر',   desc:'Dawn' },
  { id:'dhuhr',   name:'Dhuhr',   ar:'الظهر',   desc:'Noon' },
  { id:'asr',     name:'Asr',     ar:'العصر',   desc:'Afternoon' },
  { id:'maghrib', name:'Maghrib', ar:'المغرب',  desc:'Sunset' },
  { id:'isha',    name:'Isha',    ar:'العشاء',  desc:'Night' },
];

/* Seeded synchronously at module load, before React's first render, so the first
   paint shows real times rather than placeholders: a previously saved location
   if there is one, otherwise the timezone guess. The edge lookup refines this
   afterwards (md-app.jsx) — it is a network round-trip and cannot be waited on
   here. Both fall back to null, and every consumer handles that. */
let MD_LOC = (window.mdLoadLocation && window.mdLoadLocation())
  || (window.mdCityForTimezone && window.mdCityForTimezone())
  || null;
let MD_CALC = (window.mdLoadCalc && window.mdLoadCalc())
  || { method:'MuslimWorldLeague', madhab:'shafi', chosen:false };
// First run in a country whose authority publishes its own method: adopt it,
// unless the user has already chosen for themselves.
if (MD_LOC && MD_LOC.country && !MD_CALC.chosen && window.mdDefaultCalcFor) {
  MD_CALC = window.mdDefaultCalcFor(MD_LOC.country);
}
let MD_PRAYER_CACHE = new Map();                      // offset -> computed day

function mdLocation(){ return MD_LOC; }
function mdCalc(){ return MD_CALC; }
function mdSetLocation(loc){ MD_LOC = loc || null; MD_PRAYER_CACHE = new Map(); }
function mdSetCalc(calc){ MD_CALC = { ...MD_CALC, ...calc }; MD_PRAYER_CACHE = new Map(); }
// The anchor moving at midnight invalidates every cached offset.
function mdClearPrayerCache(){ MD_PRAYER_CACHE = new Map(); }

function mdPad2(n){ return String(n).padStart(2,'0'); }

/* Minutes from the START OF THE REQUESTED DAY — deliberately not
   `hours*60 + minutes`.

   At high latitude in midsummer, Isha (and sometimes Maghrib) falls after local
   midnight, so adhan correctly returns a Date on the FOLLOWING day. Reading the
   clock face off that Date gives a tiny number, which sorts the last prayer of
   the night before the first prayer of the morning and corrupts the now-line,
   the overlap model and every free-time gap. That is MANDANA-2, and measuring
   from the day's own midnight is what makes it not happen: a 00:30 Isha is
   minute 1470 of its day, not minute 30. */
function mdMinutesFromDayStart(date, dayStart){
  return Math.round((date.getTime() - dayStart.getTime()) / 60000);
}

/* Formats a Date as the "HH:MM" the UI expects, from the wall clock — this is
   the label, so it should read 00:30 for a post-midnight Isha even though its
   `min` is 1470. */
function mdClockOf(date){ return `${mdPad2(date.getHours())}:${mdPad2(date.getMinutes())}`; }

/* The whole day's sun-derived times for a day offset. Returns null when there is
   no location yet — callers must handle that rather than be handed a plausible
   fiction. */
function mdSolarFor(offset){
  if (MD_PRAYER_CACHE.has(offset)) return MD_PRAYER_CACHE.get(offset);
  const loc = MD_LOC;
  if (!loc || typeof adhan === 'undefined') { return null; }

  const dayStart = mdDateAt(offset);
  let out = null;
  try {
    const coords = new adhan.Coordinates(loc.lat, loc.lon);
    const make = adhan.CalculationMethod[MD_CALC.method] || adhan.CalculationMethod.MuslimWorldLeague;
    const params = make();
    params.madhab = MD_CALC.madhab === 'hanafi' ? adhan.Madhab.Hanafi : adhan.Madhab.Shafi;
    // Above the Arctic/Antarctic circles the sun may not cross the required angle
    // at all, leaving Fajr or Isha undefined. This picks the seventh-of-the-night
    // convention rather than rendering a day with holes in it.
    params.highLatitudeRule = adhan.HighLatitudeRule.recommended(coords);

    const pt = new adhan.PrayerTimes(coords, dayStart, params);
    const at = (d) => (d instanceof Date && !isNaN(d) ? d : null);

    const prayers = MD_PRAYER_META.map((meta) => {
      const d = at(pt[meta.id]);
      if (!d) return null;
      // `name` is what the UI renders; `ar` has been sitting in the data unused.
      return { ...meta, name: MD_LANG === 'ar' ? meta.ar : meta.name,
        t: mdClockOf(d), min: mdMinutesFromDayStart(d, dayStart), at: d };
    }).filter(Boolean);

    const sunriseD = at(pt.sunrise);
    const sunrise = sunriseD
      ? { id:'sun', name:'Sunrise', t: mdClockOf(sunriseD), min: mdMinutesFromDayStart(sunriseD, dayStart) }
      : null;

    out = prayers.length ? { prayers: prayers.sort((a,b) => a.min - b.min), sunrise } : null;
  } catch (e) {
    out = null;
  }
  MD_PRAYER_CACHE.set(offset, out);
  return out;
}

function mdPrayersFor(offset){ const s = mdSolarFor(offset); return s ? s.prayers : []; }
function mdSunriseFor(offset){ const s = mdSolarFor(offset); return s ? s.sunrise : null; }


/* ── calendar model — offsets in days from today (offset 0 = today) ────────
   The anchor was a fixed `new Date(2026, 4, 30)`. It is the real current date
   now, and because an app can be left open overnight it is a `let` that
   mdRefreshToday() advances — see the tick in md-app.jsx. */
const MD_DOW   = ['Sat','Sun','Mon','Tue','Wed','Thu','Fri'];
const MD_MON   = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MD_MON_L = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function mdStartOfDay(d){ const x = new Date(d); x.setHours(0,0,0,0); return x; }
let MD_TODAY_DATE = mdStartOfDay(new Date());

/* Advances the anchor if the calendar day has turned. Returns how many days it
   moved (0 = no change) so the caller can keep the viewed day pointing at the
   same date rather than sliding forward with the anchor. */
function mdRefreshToday(){
  const t = mdStartOfDay(new Date());
  const moved = Math.round((t.getTime() - MD_TODAY_DATE.getTime()) / 86400000);
  if (moved !== 0) MD_TODAY_DATE = t;
  return moved;
}

/* Local midnight `offset` days from today. Built by date arithmetic rather than
   by adding offset*86400000 ms: across a daylight-saving boundary a day is 23 or
   25 hours, and the ms form lands on the wrong calendar day. That never showed
   while the anchor was frozen in May; with real dates it would. */
function mdDateAt(offset){
  const t = MD_TODAY_DATE;
  return new Date(t.getFullYear(), t.getMonth(), t.getDate() + offset);
}

/* The stable identity of a day: "YYYY-MM-DD" in local time.

   Everything the user creates is filed under this, NOT under a day offset.
   An offset is relative to "today", so it names a different date every morning —
   anything stored by offset silently migrates to the wrong day at midnight, and
   a note written on Tuesday would be read back on Wednesday. */
function mdDayKey(offset = 0){
  const d = mdDateAt(offset);
  return `${d.getFullYear()}-${mdPad2(d.getMonth() + 1)}-${mdPad2(d.getDate())}`;
}

/* The offset-keyed → day-keyed repair that used to live here is gone. It was
   this file's private answer to a storage problem, written before there was a
   data layer to own one; md-store.js's migrateV0toV1 now reads the same legacy
   `md:events` / `md:records` / `md:completed` blobs, quarantines what it cannot
   resolve, and refuses to delete the source until the copy is proven. Nothing
   called this any more — md-app.jsx still destructured the name, but never
   invoked it. */

// columns run Sat(0)…Fri(6); JS getDay() is Sun(0)…Sat(6)
function mdCol(offset){ return (mdDateAt(offset).getDay() + 1) % 7; }
function mdDow(offset){ return MD_DOW[mdCol(offset)]; }
// Which Sat→Fri column today occupies. The week strip used to hardcode 6,
// because the prototype's fixed anchor was authored as a Friday; with real dates
// that put every real today under "Fri".
function mdTodayCol(){ return mdCol(0); }
function mdDateForOffset(offset){
  const d = mdDateAt(offset);
  return { d:d.getDate(), m:d.getMonth(), mon:MD_MON[d.getMonth()], monLong:MD_MON_L[d.getMonth()], year:d.getFullYear(), dow:mdDow(offset) };
}
// day-offset from today for a Gregorian (year, monthIndex, day) — inverse of mdDateForOffset
function mdOffsetForDate(y, m, d){ return Math.round((new Date(y, m, d).getTime() - MD_TODAY_DATE.getTime()) / 86400000); }
/* ── the Hijri date ─────────────────────────────────────────────────────────
   Was a hand-rolled walk over a six-month table anchored to 30 May 2026 = 14
   Dhul Hijjah 1447. It ran out about a hundred days past its anchor and then
   clamped, and its base was the suspected off-by-one.

   It is Intl now — `islamic-umalqura`, the published Saudi calendar, which the
   runtime already ships. No table, no library, no range limit, and no bytes.

   On MANDANA-3: the prototype was not simply wrong. For 30 May 2026 it showed
   14 Dhul Hijjah, which is what the ASTRONOMICAL reckoning gives; Umm al-Qura
   and the tabular/civil algorithm (what the retired Kotlin engine used, hence
   the disagreement) both give 13. Two defensible answers, not one error. We
   default to Umm al-Qura because it is what Saudi announces, what most Muslim
   apps display, and what the ±adjustment below exists to reconcile against
   local sighting.

   Month names stay the app's own transliteration, indexed by Intl's month
   NUMBER, so the calendar's typography is unchanged. */
const MD_HIJRI_MONTHS = [
  { name:'Muharram',          short:'Muḥ.'     },
  { name:'Safar',             short:'Ṣafar'    },
  { name:'Rabiʿ al-Awwal',    short:'Rabiʿ I'  },
  { name:'Rabiʿ al-Thani',    short:'Rabiʿ II' },
  { name:'Jumada al-Ula',     short:'Jum. I'   },
  { name:'Jumada al-Akhira',  short:'Jum. II'  },
  { name:'Rajab',             short:'Rajab'    },
  { name:'Shaʿban',           short:'Shaʿ.'    },
  { name:'Ramadan',           short:'Rama.'    },
  { name:'Shawwal',           short:'Shaw.'    },
  { name:'Dhul Qaʿdah',       short:'Dhuʿl-Q.' },
  { name:'Dhul Hijjah',       short:'Dhuʿl-H.' },
];

/* Two independent shifts, in days, both applied to the Gregorian date before it
   is converted:

     MD_HIJRI_ADJUST    the user's own, -2..+2, persisted. The whole point of a
                        calendar that begins on a sighting is that the authority
                        a person follows may differ from a calculation.
     MD_HIJRI_SERVED    a correction fetched from /api/hijri. Lets a sighting
                        announcement reach everyone without a redeploy, and is
                        IGNORED once the user has set their own — a person who
                        has adjusted for their community should not be overridden
                        by a central default. */
let MD_HIJRI_ADJUST = 0;
let MD_HIJRI_ADJUST_SET = false;
let MD_HIJRI_SERVED = 0;
// Seeded at module load like the location, so the first paint is already the
// user's calendar rather than the default one.
if (window.mdLoadHijriAdjust) {
  const saved = window.mdLoadHijriAdjust();
  if (saved != null) { MD_HIJRI_ADJUST = saved; MD_HIJRI_ADJUST_SET = true; }
}
function mdHijriAdjust(){ return MD_HIJRI_ADJUST_SET ? MD_HIJRI_ADJUST : MD_HIJRI_SERVED; }
function mdSetHijriAdjust(days, byUser = true){
  const n = Math.max(-2, Math.min(2, Math.round(Number(days) || 0)));
  if (byUser) { MD_HIJRI_ADJUST = n; MD_HIJRI_ADJUST_SET = true; }
  else MD_HIJRI_SERVED = n;
}
function mdHijriAdjustIsUsers(){ return MD_HIJRI_ADJUST_SET; }

const MD_HIJRI_FMT = (() => {
  try {
    return new Intl.DateTimeFormat('en-u-ca-islamic-umalqura',
      { day:'numeric', month:'numeric', year:'numeric' });
  } catch (e) { return null; }
})();

function mdHijriParts(offset){
  const base = mdDateAt(offset);
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + mdHijriAdjust());
  if (!MD_HIJRI_FMT) return { day:1, month:'', short:'', year:0, isMonthStart:false };
  let day = 1, monthNo = 1, year = 0;
  for (const part of MD_HIJRI_FMT.formatToParts(d)) {
    if (part.type === 'day') day = parseInt(part.value, 10);
    else if (part.type === 'month') monthNo = parseInt(part.value, 10);
    else if (part.type === 'year') year = parseInt(part.value, 10);
  }
  const m = MD_HIJRI_MONTHS[Math.min(11, Math.max(0, monthNo - 1))];
  return { day, month:m.name, short:m.short, year, isMonthStart: day === 1 };
}
function mdHijri(offset){ const p = mdHijriParts(offset); return `${p.day} ${p.month} ${p.year}`; }

/* ── time helpers, all TOTAL ────────────────────────────────────────────────
   These are called during render, once per card. A throw here does not spoil one
   card — React unwinds the whole tree and the user gets a blank screen on every
   load, with no way out but clearing site data. That was tolerable when every
   record came from the compose sheet, which always fills these fields in. It is
   not tolerable now: records arrive from other devices, from older versions of
   this client, and from a server none of them control. So each of these returns
   a placeholder for input it cannot read, and never throws. */
function mdMinOf(t){
  if (typeof t !== 'string') return null;
  const [h, m] = t.split(':');
  const H = Number(h), M = Number(m);
  if (!Number.isFinite(H) || !Number.isFinite(M)) return null;
  return H * 60 + M;
}

/* THE prayer window: a prayer holds its minute for half an hour, and the end is
   rounded UP to the next five-minute boundary so it lands on the same grid the
   app shows and schedules on. Rounding to the NEAREST boundary instead would
   end the window BEFORE the half hour is out two minutes in five — which is how
   "start after Fajr" once offered 5:20 for a window ending 5:25, putting work on
   top of the prayer. Prayer is the axis, so this rounds one way only, and every
   surface that draws or schedules around a prayer calls this rather than
   carrying its own copy: md-today.jsx, md-grid.jsx, md-datepicker.jsx
   (tests/prayer-window.test.js). */
function mdPrayerWindowEnd(min){ return Math.ceil((Number(min) + 30) / 5) * 5; }

// merged, ordered day items for a given day offset (0 = today)
function mdDayItems(offset = 0){
  const arr = [];
  mdPrayersFor(offset).forEach(p => { const dur = mdPrayerWindowEnd(p.min) - p.min; arr.push({ ...p, kind:'prayer', dur, uid: p.id }); });
  // No events here any more. They used to come from MD_WEEK_EVENTS, a fixed
  // Sat-Fri set of demo appointments that repeated every week forever. The day's
  // events are the user's own now and are supplied by md-app.jsx from the stored
  // day; this function returns only what the sun decides.
  const sunrise = mdSunriseFor(offset);
  if (sunrise) arr.push({ ...sunrise, kind:'sun', dur:0, uid:'sun' });
  return arr.sort((a,b)=> a.min - b.min);
}

/* ── Escape closes whatever is on top ────────────────────────────────────────
   Every overlay in this app — the compose sheet, Settings, the reschedule sheet,
   the confirm dialog — could only be dismissed by TAPPING its scrim. With a keyboard
   there was no way out at all: focus enters the surface and stays there. That is
   a keyboard trap, and it is the one accessibility failure serious enough to make
   a page unusable rather than merely awkward.

   Capture phase, so the surface that mounted last wins and a stack of overlays
   unwinds one at a time rather than all at once. */
/* useEscape lived here for five phases: a window-capture listener per mounted
   surface, every instance firing, order decided by mount accident. Deleted in
   N-2 — dismissal is the arbiter's (md-dismiss.jsx), one stack, top first. */

/* ── last resort ────────────────────────────────────────────────────────────
   The layers above stop the failures we can name. This one exists for the ones
   we cannot, and its job is narrow but important: keep the failure INSIDE the
   day view.

   React unmounts the entire tree when a render throws, so without a boundary one
   unreadable card takes down the header, the week strip, the More menu and
   Settings along with it — and Settings is where Export, Sign out and Delete
   account live. That is the difference between "today looks wrong" and "the only
   way out is clearing site data". Wrapped here, the shell survives and the user
   can still get at their data.

   It does not attempt to recover. A render that threw once will throw again on
   the same input; retrying just flickers. Changing day remounts it via `resetKey`,
   which is the honest way back. */
class MDBoundary extends React.Component {
  constructor(props){ super(props); this.state = { failed: false }; }
  static getDerivedStateFromError(){ return { failed: true }; }
  componentDidUpdate(prev){
    if (this.state.failed && prev.resetKey !== this.props.resetKey) this.setState({ failed: false });
  }
  componentDidCatch(err){ try { console.error('[ManDana] day view failed to render', err); } catch (e) {} }
  render(){
    if (!this.state.failed) return this.props.children;
    return this.props.fallback || null;
  }
}

/* ───────────────────────── HELPERS ───────────────────────── */
function mdRgba(hex,a){ const h=hex.replace('#',''); const n=parseInt(h.length===3?h.split('').map(x=>x+x).join(''):h,16); return `rgba(${(n>>16)&255},${(n>>8)&255},${n&255},${a})`; }
function mdHexToRgb(hex){ const h=hex.replace('#',''); const n=parseInt(h.length===3?h.split('').map(x=>x+x).join(''):h,16); return [(n>>16)&255,(n>>8)&255,n&255]; }
function mdRgbToHsl(r,g,b){ r/=255;g/=255;b/=255; const max=Math.max(r,g,b),min=Math.min(r,g,b); let h,s,l=(max+min)/2; if(max===min){h=s=0;} else { const d=max-min; s=l>0.5?d/(2-max-min):d/(max+min); switch(max){case r:h=(g-b)/d+(g<b?6:0);break;case g:h=(b-r)/d+2;break;default:h=(r-g)/d+4;} h/=6; } return [h*360,s,l]; }
function mdHslToHex(h,s,l){ h/=360; let r,g,b; if(s===0){r=g=b=l;} else { const hue2rgb=(p,q,t)=>{ if(t<0)t+=1; if(t>1)t-=1; if(t<1/6)return p+(q-p)*6*t; if(t<1/2)return q; if(t<2/3)return p+(q-p)*(2/3-t)*6; return p; }; const q=l<0.5?l*(1+s):l+s-l*s, p=2*l-q; r=hue2rgb(p,q,h+1/3);g=hue2rgb(p,q,h);b=hue2rgb(p,q,h-1/3); } const to=x=>Math.round(x*255).toString(16).padStart(2,'0'); return '#'+to(r)+to(g)+to(b); }

/* ── WHAT COLOUR THE BACKDROP ACTUALLY IS ───────────────────────────────────
   The full-bleed backdrop (md-app.jsx Background) is a gradient with a black
   scrim over it, and more than one surface needs to know what that adds up to.
   The pinned day header is the loud case: it paints a solid material because a
   floating panel has no opaque page behind it and the cards scrolling under it
   have to disappear rather than smear — and a solid material that is not the
   backdrop's colour is a rectangle.

   It WAS a constant. rgba(12,12,14,0.72) was measured by eye against
   hsl(228 18% 5%) = rgb(10 11 15), and it was a good match for exactly as long
   as that was the backdrop. Then bgBlackness shipped at 1, the scrim covered
   the gradient, the backdrop became rgb(0 0 0), and the constant stayed put —
   a lighter block on the void, full panel width, square behind a card whose own
   fill is transparent by design.

   So the number is derived here instead of remembered there. The tables live in
   this file rather than in the shell because they are palette, and because the
   shell is not loadable by a test without booting the store.

   The TOP stop is what these answer with: the gradient runs lighter down the
   page, but the header sits at the top, and at blackness 1 — what ships — the
   whole thing is one flat black anyway.

   Channels, space-separated, not a colour: the caller picks the alpha, via
   rgb(var(--md-backdrop-rgb) / 0.72). Going through mdHslToHex on the way is
   deliberate — the browser quantizes the gradient stop to 8-bit before the
   scrim composites over it, so rounding in the same order models what is
   actually on screen. */
const MD_LIGHT_GRAD = {
  cool:    ['#fafbfc', '#f4f6f8', '#edf0f3'],
  warm:    ['#fbfbfa', '#f5f3f0', '#eeece8'],
  violet:  ['#fbfafc', '#f5f2f8', '#eeebf2'],
  neutral: ['#fafafa', '#f4f4f5', '#ededee'],
};
const MD_DARK_HUE = { cool: [228, 18], warm: [32, 22], violet: [266, 22], neutral: [228, 6] };

/** The three light-theme gradient stops. Unknown preset falls back to cool. */
function mdLightGrad(gradient){ return MD_LIGHT_GRAD[gradient] || MD_LIGHT_GRAD.cool; }

/** [H, S, L] of the dark backdrop's top stop. `level` is the base lightness. */
function mdDarkHSL(gradient, level = 5){
  const [H, S] = MD_DARK_HUE[gradient] || MD_DARK_HUE.cool;
  return [H, S, level];
}

/** Dark backdrop as "r g b", with the black scrim composited at `blackness`. */
function mdBackdropRGB(gradient, blackness = 0, level = 5){
  const [H, S, L] = mdDarkHSL(gradient, level);
  const k = 1 - Math.min(1, Math.max(0, Number(blackness) || 0));
  return mdHexToRgb(mdHslToHex(H, S / 100, L / 100)).map((c) => Math.round(c * k)).join(' ');
}

/** The backdrop as a CSS colour, for a surface that has to match it.

    `rgb(var(--x) / a)` is the modern form and every engine we ship to resolves
    it — var() substitution happens at computed-value time, so the rgb() cannot
    be validated (or rejected) at parse time. That same property is why happy-dom
    drops the declaration entirely and no render test can see it, which is why
    this returns a STRING the tests pin exactly.

    The fallback is the constant this replaced. It applies when the property was
    never set at all — a surface mounted with no app shell above it, which is
    every surface test in tests/ — so those keep rendering what they always did.
    A blackness of 1 makes the token "0 0 0"; the fallback is not a second
    opinion about the backdrop, it is what to draw when nobody has said. */
function mdBackdropCSS(alpha = 1){
  const token = 'var(--md-backdrop-rgb, 12 12 14)';
  return alpha >= 1 ? `rgb(${token})` : `rgb(${token} / ${alpha})`;
}

/* The same question for whichever theme is on. Blackness is a DARK-ONLY tweak
   (see the note on Glass), so the light answer ignores it entirely — the light
   backdrop is the gradient it has always been. */
function mdBackdropToken(light, gradient, blackness = 0, level = 5){
  if (!light) return mdBackdropRGB(gradient, blackness, level);
  return mdHexToRgb(mdLightGrad(gradient)[0]).join(' ');
}

// global icon-color vibrancy (1 = original saturation). Scales a hex's S in HSL.
let MD_VIBRANCY = 1;
function mdSetVibrancy(v){ MD_VIBRANCY = (v==null ? 1 : v); }
function mdVivid(hex, factor=MD_VIBRANCY){ if(typeof hex!=='string' || hex[0]!=='#' || factor===1) return hex; const [h,s,l]=mdRgbToHsl(...mdHexToRgb(hex)); return mdHslToHex(h, Math.max(0,Math.min(1,s*factor)), l); }

/* ───────────────────────── THEME (light / dark) ───────────────────────── */
// MD_LIGHT toggled by the app. ink() = foreground (text/dividers/tiles),
// surf() = glass surface tint, onAcc() = text that sits on an accent fill.
let MD_LIGHT = false;
const MD_INK_LIGHT = [30, 34, 42];      // neutral charcoal ink for light theme
/* iOS 26 Safari computes a "scroll edge" appearance — the material it paints
   behind the status bar and around the collapsed address capsule — PER
   fixed/sticky box, once, when the box first appears. A theme change restyles
   those boxes in place, and that cache is never invalidated: switch the app to
   dark and Safari keeps painting LIGHT bands top and bottom; switch to light
   and the bands stay black. The page's own word carries no weight against the
   cache — html/body backgrounds, color-scheme and the theme-color meta were
   all verified correct while the bands stood wrong.

   Bisected in the iOS 26.5 Simulator (2026-08-28): re-setting the theme-color
   meta does not reach the cache; adding a NEW fixed element does not;
   rebuilding the existing boxes does. So, two frames after the theme is
   stamped (the new dress has painted), every fixed/sticky box is hidden for
   one frame and restored — Safari re-samples on reappearance and the bands
   follow the theme. Inline display is saved and restored verbatim, so React's
   own styles are untouched. Apple-WebKit-only: no other engine paints from
   this cache, and no other engine should pay the one-frame blink. */
function mdResampleSafariChrome(){
  try {
    if (!(navigator.vendor || '').startsWith('Apple')) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const held = [];
      document.querySelectorAll('body *').forEach((e) => {
        const cs = getComputedStyle(e);
        if (cs.position === 'fixed' || cs.position === 'sticky') {
          held.push([e, e.style.display]);
          e.style.display = 'none';
        }
      });
      if (!held.length) return;
      requestAnimationFrame(() => requestAnimationFrame(() => {
        held.forEach(([e, d]) => { e.style.display = d; });
      }));
    }));
  } catch (e) {}
}
/* ── THE GROUND A PHONE'S FULL-SCREEN SURFACE MEETS ────────────────────────
   A phone's screen is taller than the page it hands us. On an iPhone 17e the
   layout viewport is 699 points of 844, and Safari keeps the rest: the strip
   behind the status bar and the strip around the address capsule. It fills
   them with a material of its own, and the page does not get a vote — measured
   in the iOS 26.5 Simulator (2026-08-28), setting the theme-color meta, the
   html background and the body background all to a loud red moved neither
   strip, and rebuilding the fixed boxes moved only the top one, to a colour
   nobody had asked for. Within a theme those strips are simply the void.

   Which is why the day reads edge to edge and every other surface did not.
   The day paints straight onto the void, so its strips and its surface are the
   same colour and the screen has no edges in it. The frosted sheets composite
   LIGHTER — rgb(14,16,20) against the strips' rgb(8,9,12), measured — and that
   difference, top and bottom, is the "arbitrary tops and bottoms".

   So the app matches the strips rather than the other way round: on a phone a
   full-screen surface sits on the void, as the day always has. Nothing is lost
   in the trade. A sheet that fills a phone covers the whole screen, so the
   material its frost blurs is a layer no one can see, and the only part of the
   frost that survives is the shade that makes the seam. Everywhere the frost
   is really doing its work — a panel floating beside a workspace, a window
   over a canvas, any screen wide enough to show what is behind it — it stays
   exactly as it was.

   Published as a custom property so the phone's rule reads the value from
   here rather than restating it. */
function mdChromeVoid(light){ return light ? '#F6F7F8' : '#08090C'; }
function mdSetLight(b){
  const changed = MD_LIGHT !== !!b;
  MD_LIGHT = !!b;
  // Mirror the theme onto the document's color-scheme. iOS picks the software
  // keyboard's appearance from this — undeclared means "light", which put a
  // white keyboard under the dark app. Same idiom as mdApplyLang: the document
  // element carries what the platform reads.
  try {
    const chrome = mdChromeVoid(MD_LIGHT);
    document.documentElement.style.colorScheme = MD_LIGHT ? 'light' : 'dark';
    document.documentElement.style.backgroundColor = chrome;
    document.documentElement.style.setProperty('--md-chrome-void', chrome);
    if (document.body) document.body.style.backgroundColor = chrome;
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', chrome);
    if (changed) mdResampleSafariChrome();
  } catch (e) {}
}

/* ── the user's appearance choice ────────────────────────────────────────────
   Kept OUT of the Tweaks store. Tweaks is design tooling and is stubbed away in
   production, where its state does not persist — a theme chosen there would be
   forgotten on the next load. This is a user preference like place or
   calculation method, so it is stored and read like one.

   Deliberately NOT synced across devices: which theme suits a phone in a bright
   room has nothing to do with the laptop it is signed in beside. Every other
   preference syncs; this one is about the screen you are holding.

   Default stays dark, which is what the prototype was drawn in. */
const MD_THEME_KEY = 'md:theme';
/* Raw, not JSON: the stored value is the bare word `dark`, and has been on every
   device since R1. mdRead/mdWrite would re-encode it as `"dark"`, which the
   check below then rejects — every existing user silently back to the default
   on their next launch. The Raw pair is the compatible one. */
function mdLoadTheme(){
  const v = window.mdReadRaw(MD_THEME_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : null;
}
function mdSaveTheme(v){ return window.mdWriteRaw(MD_THEME_KEY, v); }

/* ── System: the third answer ────────────────────────────────────────────────
   Light and Dark are choices. System is a standing instruction — follow this
   device — and the difference costs code in exactly two places: at boot, where
   a stored word has to become a real theme, and at sunset, where the device
   changes its mind while the app is open.

   The turning is a PURE function over two plain values, so all six combinations
   are provable with no browser in the room. An unrecognised word — a key written
   by a build this one has never met, or a corrupted one — resolves to dark,
   which is what this app has defaulted to since R1.

   That default is also why `system` is safe to write. A device still running an
   older build reads the word, fails its two-value check, and falls back to dark:
   the same answer from the other direction, on a key that syncs nowhere. */
function mdThemeResolve(stored, prefersDark){
  if (stored === 'light' || stored === 'dark') return stored;
  if (stored === 'system') return prefersDark ? 'dark' : 'light';
  return 'dark';
}
/* The word the user chose, for the one screen that shows it back to them.
   Everything else asks mdIsLight(), which is the resolved answer. */
function mdThemeChoice(){
  const v = window.mdReadRaw(MD_THEME_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : null;
}
function mdPrefersDark(){
  try { return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches); }
  catch (e) { return false; }
}
/* The app's only prefers-color-scheme listener, and it is deliberately
   indifferent to which theme is chosen: it reports what the system says and
   lets mdThemeResolve decide whether that changes anything. Returns its own
   undo. Safari before 14 has no addEventListener on a MediaQueryList, hence the
   deprecated pair as a fallback — a phone that old is exactly the phone whose
   owner never opens Settings. */
function mdOnSystemTheme(fn){
  let q = null;
  try { q = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)'); } catch (e) { q = null; }
  if (!q || typeof fn !== 'function') return () => {};
  const hear = (e) => { try { fn(!!(e && 'matches' in e ? e.matches : q.matches)); } catch (err) {} };
  if (q.addEventListener) { q.addEventListener('change', hear); return () => { try { q.removeEventListener('change', hear); } catch (e) {} }; }
  if (q.addListener) { q.addListener(hear); return () => { try { q.removeListener(hear); } catch (e) {} }; }
  return () => {};
}

/* ── language and writing direction ──────────────────────────────────────────
   Arabic is the language this app's users pray in, and the prayer names already
   carry their Arabic forms in MD_PRAYER_META. This is the plumbing: a stored
   preference, the document's lang and dir, and the switch that drives both.

   R6 scope is RTL-READY, not translated. The interface stays English while the
   prayer names become Arabic — an odd halfway state to look at, but the point is
   that the later translation pass is translation, not surgery.

   Like the theme, this is per-device and does not sync. */
const MD_LANG_KEY = 'md:lang';
// Raw for the same reason as the theme above.
function mdLoadLang(){
  const v = window.mdReadRaw(MD_LANG_KEY);
  return v === 'ar' || v === 'en' ? v : null;
}
function mdSaveLang(v){ return window.mdWriteRaw(MD_LANG_KEY, v); }
let MD_LANG = mdLoadLang() || 'en';
/* Direction is not read from here. mdApplyLang() stamps documentElement.dir and
   everything downstream asks the DOM — md-markdown.jsx reads
   getComputedStyle(body).direction, the stylesheet keys off :root[dir="rtl"].
   A second source of truth for "are we RTL" is one that can disagree. */
function mdLang(){ return MD_LANG; }
function mdSetLang(v){
  MD_LANG = v === 'ar' ? 'ar' : 'en';
  mdSaveLang(MD_LANG);
  mdApplyLang();
  mdClearPrayerCache();      // prayer names are baked into the cached day
}
/* dir belongs on the document element: it drives the browser's own bidi
   handling, logical CSS properties, and text alignment defaults all at once. */
function mdApplyLang(){
  try {
    document.documentElement.lang = MD_LANG;
    document.documentElement.dir = MD_LANG === 'ar' ? 'rtl' : 'ltr';
  } catch (e) {}
}
mdApplyLang();
function mdIsLight(){ return MD_LIGHT; }
// dark-theme glass tunables, driven by Tweaks. fill = base white-film opacity
// (old per-card default was 0.12), blur = minimum backdrop frost, shadow = drop
// shadow opacity. Light theme ignores these entirely.
let MD_GLASS = { fill:0.07, blur:46, halo:0.06, stroke:0.22, sheen:0.55, glow:0, shadow:1, blurMul:1 };
function mdSetGlass(o){ if(o) MD_GLASS = { ...MD_GLASS, ...o }; }
function ink(a){ return MD_LIGHT ? `rgba(${MD_INK_LIGHT[0]},${MD_INK_LIGHT[1]},${MD_INK_LIGHT[2]},${a})` : `rgba(255,255,255,${a})`; }
function surf(a){ return MD_LIGHT ? `rgba(255,255,255,${Math.min(0.94, 0.6 + a)})` : `rgba(255,255,255,${a * (MD_GLASS.fill / 0.07)})`; }
function onAcc(){ return '#fff'; }
// pale picker swatches → vivid, saturated accent used in BOTH themes so the
// accent reads the same rich tone on dark as it does on light.
const MD_ACCENT_DEEP = { '#FFC978':'#E8972B', '#7FE3FF':'#1FAFD6', '#9BE8C2':'#22B179', '#FFB3C7':'#EC6488', '#C4B5FF':'#7C68E6', '#2EE6A6':'#0FA877' };
function mdAccentFor(hex, light){ return MD_ACCENT_DEEP[hex] || hex; }
const MD_NO_TIME = '—';
function mdFmt(t){
  if (typeof t !== 'string') return MD_NO_TIME;
  const [h, m] = t.split(':');
  const H0 = Number(h);
  if (!Number.isFinite(H0) || m == null || !/^\d{1,2}$/.test(m.trim())) return MD_NO_TIME;
  const ap = H0 >= 12 ? 'PM' : 'AM';
  return `${H0 % 12 || 12}:${m} ${ap}`;
}
function mdEnd(min,dur){
  const e = Number(min) + Number(dur);
  if (!Number.isFinite(e)) return MD_NO_TIME;
  const H = Math.floor(e / 60), M = e % 60;
  const ap = H >= 12 ? 'PM' : 'AM';
  return `${H % 12 || 12}:${String(M).padStart(2, '0')} ${ap}`;
}
function mdHM(mins){
  const n = Number(mins);
  if (!Number.isFinite(n)) return MD_NO_TIME;
  const h = Math.floor(n / 60), m = n % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
// next prayer relative to a "now" in minutes (+ seconds for live countdown)
function mdNextPrayer(nowMin){
  const today = mdPrayersFor(0);
  for(const p of today){ if(p.min > nowMin) return p; }
  // Past the last prayer of today — wrap to TOMORROW's Fajr, computed for
  // tomorrow rather than reusing today's, because it moves by a minute or two a
  // day and the countdown would drift against it.
  const tomorrow = mdPrayersFor(1);
  return tomorrow[0] || today[0] || null;
}

/* ───────────────────────── ICONS ───────────────────────── */
const I = {
  project:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="project"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <path d="M12 3.5l7.5 4.3v8.4L12 20.5l-7.5-4.3V7.8z" />
    <path d="M12 12v8.5M12 12 4.7 7.9M12 12l7.3-4.1" />
  </svg>,
  database:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="database"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <rect x="4" y="4.5" width="16" height="15" rx="3" />
    <path d="M4 10h16M4 15h16M10 10v9.5" />
  </svg>,
  desk:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="desk"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    {/* The L-desk seen from above, and the seat in its notch (Marwan, 2026-08-24,
        spec verbatim). It replaces a drawing board on legs — a desk drawn from
        the SIDE, which is the one angle every other glyph here is not: a project
        is a solid seen in three-quarter, a database and a day are plans. Drawn
        at the set's 1.8 rather than the spec's 2, because "the same weights as
        ManDana's icons" is the half of the instruction a stroke width answers. */}
    <path d="M4.5 4h15A1.5 1.5 0 0 1 21 5.5v14a1.5 1.5 0 0 1-1.5 1.5h-5a1.5 1.5 0 0 1-1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5h-7A1.5 1.5 0 0 1 3 9.5v-4A1.5 1.5 0 0 1 4.5 4z" />
    <circle cx="8" cy="16.2" r="2.4" />
  </svg>,
  check:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M5 12.5l4.2 4.2L19 7" stroke={c} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  bell:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M6 9a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/><path d="M10 19a2 2 0 004 0" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  bellOff:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M6 9a6 6 0 0110-4.3M18 9c0 5 2 6 2 6H8" stroke={c} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/><path d="M10 19a2 2 0 004 0M3 3l18 18" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  clock:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="clock"
    width={w} height={w} viewBox="0 0 24 24" fill="none" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.8"/><path d="M12 7v5.5l3.5 2" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chart:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M5 19V11M12 19V5M19 19v-6" stroke={c} strokeWidth="2" strokeLinecap="round"/></svg>,
  gear:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3.2" stroke={c} strokeWidth="1.8"/><path d="M12 3v2.5M12 18.5V21M21 12h-2.5M5.5 12H3m14.5-6.5l-1.8 1.8M8.3 15.7l-1.8 1.8m11 0l-1.8-1.8M8.3 8.3L6.5 6.5" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  /* THE NEEDLE WAS DRAWN FOR A BOX NOBODY USES IT AT. Its lens was 2 units
     across — 1.4px at the 17 the Settings column draws it — so the mark read
     as an empty ring with a smudge in it, on the one row this round took the
     answer off. Widened to 4.2 across (3px), the ring brought in from r 9 to
     the 8.2 the disc and the globe share, and the 0.9 opacity dropped: this
     ink already carries alpha, and dimming it twice is how a mark ends up
     fainter than the word beside it. The needle stops 1.1 short of the ring's
     inner edge, so nothing overlaps and nothing lights up. */
  compass:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="compass" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.2" stroke={c} strokeWidth="1.8"/><path d="M16.4 7.6L14.1 14.1L7.6 16.4L9.9 9.9Z" fill={c}/></svg>,
  chevron:(c='#fff',w=14)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 12 20" fill="none"><path d="M3 2l8 8-8 8" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chevDown:(c='#fff',w=14)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 20 12" fill="none"><path d="M2 3l8 8 8-8" stroke={c} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  sun:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="4" stroke={c} strokeWidth="1.8"/><path d="M12 2v2M12 20v2M22 12h-2M4 12H2m15.5-5.5l-1.4 1.4M7.9 16.1l-1.4 1.4m11.2 0l-1.4-1.4M7.9 7.9L6.5 6.5" stroke={c} strokeWidth="1.6" strokeLinecap="round"/></svg>,
  moon:   (c='#fff',w=16)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M20 14.5A8 8 0 019.5 4 7 7 0 1020 14.5z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/></svg>,
  plus:   (c='#fff',w=20)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={c} strokeWidth="2.2" strokeLinecap="round"/></svg>,
  loc:    (c='#fff',w=15)=><svg aria-hidden="true" focusable="false" data-md-glyph="loc" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M12 22s7-6.5 7-12a7 7 0 10-14 0c0 5.5 7 12 7 12z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/><circle cx="12" cy="10" r="2.6" stroke={c} strokeWidth="1.8"/></svg>,
  /* crosshair — "use where this device says it is". A ring with four ticks
     breaking the frame, which is the shape every map app has taught people to
     read as "find me" rather than "here is a place". MDI.loc is the pin: it
     names a place. This one asks. */
  crosshair:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="crosshair" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="6.4" stroke={c} strokeWidth="1.8"/><circle cx="12" cy="12" r="1.7" fill={c}/><path d="M12 1.8v3.3M12 18.9v3.3M22.2 12h-3.3M5.1 12H1.8" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  /* person — a head and the line of a shoulder. The set had no figure at all,
     which was fine while nothing in the app was ABOUT a person; the profile
     screen is. Deliberately featureless: a face drawn at 22px becomes an
     expression, and an expression is a mood the screen did not ask for. */
  person: (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="3.7" stroke={c} strokeWidth="1.8"/><path d="M4.8 20a7.2 7.2 0 0114.4 0" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  /* key — the ways into an account. A passkey has no picture of its own and a
     fingerprint would be a lie on a device that signs in with a PIN, so this is
     the oldest drawing of the idea and the one nobody has to be taught. */
  key:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="key" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="8" cy="12" r="4.3" stroke={c} strokeWidth="1.8"/><path d="M12.3 12H21M18.4 12v3.1M15.5 12v2.2" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  flame:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M12 3s5 4 5 9a5 5 0 01-10 0c0-1.5.6-2.7 1.3-3.6C9 10 9.5 11 10.5 11c.8 0 1-.7.5-1.8C10 7.5 11 4.5 12 3z" stroke={c} strokeWidth="1.7" strokeLinejoin="round"/></svg>,
  star:   (c='#fff',w=14)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill={c}><path d="M12 3l2.5 5.3 5.8.8-4.2 4 1 5.7L12 16.9 6.9 19.6l1-5.7-4.2-4 5.8-.8z"/></svg>,
  /* ── the five a section bar leads with ────────────────────────────────────
     `clock` above marks Recents and was already here. These are the rest, in
     the same hand as the set they join: a 24 box, no fill, 1.8 stroke, round
     ends. A row of section bars shows them side by side, so a borrowed icon
     at a borrowed weight would be the one thing in that column that looks
     imported. `starLine` is the outline twin of the filled `star` — Favorites
     wants the bar's ink weight, not a solid mark shouting from the heading. */
  starLine:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="starLine"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.7"
    strokeLinejoin="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><path d="M12 3.7l2.4 5 5.4.8-3.9 3.8.9 5.4-4.8-2.5-4.8 2.5.9-5.4-3.9-3.8 5.4-.8z"/></svg>,
  // Properties: a list of names, each answered — which is what the section is.
  fields: (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="fields"
    width={w} height={w} viewBox="0 0 24 24" fill="none" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <circle cx="5.4" cy="7" r="1.35" fill={c}/><circle cx="5.4" cy="12" r="1.35" fill={c}/>
    <circle cx="5.4" cy="17" r="1.35" fill={c}/>
    <path d="M10 7h9.5M10 12h9.5M10 17h9.5" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  // Backlinks: something out there arrives and points AT this record.
  backlink:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="backlink"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <path d="M20 12H8.5"/><path d="M12 8.5 8.5 12l3.5 3.5"/><path d="M4.6 5.5v13"/></svg>,
  /* Related: two records, and the edge between them. Side by side rather than
     on the diagonal — drawn corner to corner the whole figure collapses into
     one squiggle at the 13px a section bar draws it. */
  related:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="related"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <circle cx="6.4" cy="12" r="3.3"/><circle cx="17.6" cy="12" r="3.3"/>
    <path d="M9.7 12h4.6" strokeLinecap="round"/></svg>,
  // Tags: the hash the app already writes a tag with, in the same hand.
  tag:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="tag"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><path d="M9.8 4 7.9 20M16.4 4l-1.9 16M4 9.2h15.6M3.6 14.8h15.6"/></svg>,
  /* Workflows: a branch — one line of work, and the path that leaves it for
     another place to stand. Not the dining `fork` below, which this set keeps
     for food; the settings sheet titles its Workflows screens with this. */
  workflow:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="workflow"
    width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}>
    <circle cx="6" cy="18" r="2.7"/><circle cx="18" cy="6" r="2.7"/>
    <path d="M6 3v12.3"/><path d="M18 8.7a9.3 9.3 0 0 1-9.3 9.3"/></svg>,
  // the ManDana droplet — the mark itself (public/mandana-mark.svg), geometry and
  // gradients verbatim, viewBox cropped to the droplet body; the asset's halo is
  // left behind because the tile under it supplies the glow. It carries its own
  // colours, so unlike the rest of the set it takes no ink — size only.
  droplet:(w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="52 49 96 96"><defs><radialGradient id="mdMarkBody" cx="0.42" cy="0.36" r="0.85"><stop offset="0" stopColor="#FFFFFF"/><stop offset="0.55" stopColor="#F4F1FA"/><stop offset="1" stopColor="#CDC0E6"/></radialGradient><radialGradient id="mdMarkSpec" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stopColor="#FFFFFF"/><stop offset="1" stopColor="#FFFFFF" stopOpacity="0"/></radialGradient></defs><path d="M100 50 C102.8 68 126 87 126 111 C126 131 113 144 100 144 C87 144 74 131 74 111 C74 87 97.2 68 100 50 Z" fill="url(#mdMarkBody)"/><path d="M88 88 C82 98 80 111 85 124" fill="none" stroke="#FFFFFF" strokeWidth="3.2" strokeLinecap="round" opacity="0.95"/><path d="M105 68 C112 78 119 90 120 104" fill="none" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" opacity="0.55"/><path d="M95 139 C105 140 115 135 120 125" fill="none" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" opacity="0.45"/><ellipse cx="100" cy="112" rx="11" ry="14" fill="url(#mdMarkSpec)" opacity="0.8"/></svg>,
  dumbbell:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M3 9.5v5M6 7v10M18 7v10M21 9.5v5M6 12h12" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  fork:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M7 3v18M4.8 3v6a2.2 2.2 0 004.4 0V3M16.5 3c-1.6 0-2.8 2.7-2.8 6 0 2.5 1 3.6 2.3 3.9V21" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  video:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><rect x="3" y="6.5" width="12.5" height="11" rx="2.4" stroke={c} strokeWidth="1.8"/><path d="M15.5 10l5.5-3v10l-5.5-3z" stroke={c} strokeWidth="1.8" strokeLinejoin="round"/></svg>,
  flag:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M6 21V4" stroke={c} strokeWidth="1.8" strokeLinecap="round"/><path d="M6 4.5h11.5l-2.4 3.6 2.4 3.6H6" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  pen:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M4 20l4.2-1L19 8.2a2 2 0 00-3-3L5.2 16 4 20z" stroke={c} strokeWidth="1.7" strokeLinejoin="round"/><path d="M14 7.2l3 3" stroke={c} strokeWidth="1.7"/></svg>,
  coffee: (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M4 8.5h12.5V14a5 5 0 01-5 5H9a5 5 0 01-5-5V8.5z" stroke={c} strokeWidth="1.7" strokeLinejoin="round"/><path d="M16.5 9.5H19a2.5 2.5 0 010 5h-2.5" stroke={c} strokeWidth="1.7" strokeLinejoin="round"/><path d="M7.5 3.2c-.5.8-.5 1.6 0 2.4M11 3.2c-.5.8-.5 1.6 0 2.4" stroke={c} strokeWidth="1.5" strokeLinecap="round"/></svg>,
  // intersection mark — two overlapping circles (the literal symbol for overlap)
  overlap:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="9.2" cy="12" r="5.7" stroke={c} strokeWidth="1.7"/><circle cx="14.8" cy="12" r="5.7" stroke={c} strokeWidth="1.7"/></svg>,
  // trash — soft rounded bin for the delete affordance
  trash:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M4 7h16M9.5 7V5.4A1.4 1.4 0 0110.9 4h2.2a1.4 1.4 0 011.4 1.4V7" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M6.6 7l.8 11.5A2 2 0 009.4 20.4h5.2a2 2 0 002-1.9L17.4 7" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M10.3 10.5v6M13.7 10.5v6" stroke={c} strokeWidth="1.7" strokeLinecap="round"/></svg>,
  /* reset — two circular arrows, and THE ONLY MARK IN THE APP THAT TURNS.
     Everything else here is judged standing still, where being a little off
     the middle is a feeling nobody can name. Spun, it is a measurement: an arc
     hung off the wrong centre orbits rather than rotates, and the mark wanders
     round the row while it works.

     WHICH IS WHAT THIS WAS DOING (Marwan, 2026-09-01: "perfectly round not
     stretched and elliptical"). It was two arcs that were never on one circle
     — the upper ran (18.5, 11) → (6.8, 6.8) at radius 7, but those points sit
     6.58 and 7.35 from the middle of the box, so it implied a circle somewhere
     else and the lower arc implied a third — each finishing in a straight tail
     that left the curve, under a pair of corner brackets standing in for
     arrowheads. Three curvatures and two straight lines read as one squashed
     ring.

     Now it is ONE circle: r 6.4 about (12, 12), two 135° arcs, and the two
     gaps put on the DIAGONAL rather than at the sides. That last part is what
     makes it read round at rest — arrowheads breaking out at 3 and 9 o'clock
     stretch the silhouette sideways into exactly the oval being complained
     about, while on the diagonal they lengthen both axes at once, and the mark
     ends up 15.91 wide by 14.30 tall instead of the half-again it was. Each
     head is the 180° rotation of the other about the centre, so the thing has
     rotational symmetry and turns on its own middle rather than orbiting it.

     THE HEADS ARE FILLED, AND THAT IS FORCED, twice over. A V of two barbs
     needs barbs three or four times the stroke to read as an arrow; drawn
     short enough to stay near the ring they are two round-capped blobs, which
     is what the first attempt at this looked like at 17px. And the colour
     arrives with alpha in it (0.72), so any two strokes crossing composite to
     0.92 and the crossing lights up — the arc had a bright pip at every
     vertex. A wedge solves both: it reads at a glance because it is solid, and
     it abuts the arc instead of crossing it. Which is why each arc stops 1.3
     units of ARC — 11.6°, travelled along the circle and not off down the
     tangent — short of its head, leaving the round cap to meet the wedge's
     base.

     AND IT IS SIZED FOR 17px, WHICH IS THE ONLY SIZE IT IS EVER DRAWN AT. The
     first wedge was proportioned by eye at 240px and disappeared entirely on
     the row: the mark went back to reading as a bare ring, which is a spinner
     saying wait, not an arrow saying again. So the head is short and wide
     rather than long and fine — 3.9 units by 4.8, which is 2.8 real pixels by
     3.4 — and the ring gave up 45° at each gap to make room for it. A ring
     that is three-quarters drawn still reads as a circle; a head under three
     pixels reads as nothing.

     1.5 rather than 1.9: at 17px that is 1.06 real pixels against the
     chevron's 0.99 beside it. A circle is more line than a chevron is, so
     equal stroke is not equal weight — this sits a hair above the lightest
     mark in the column rather than a third above the heaviest. */
  reset:  (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="reset" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M14.37 17.95A6.4 6.4 0 0 1 6.13 9.46M9.63 6.05A6.4 6.4 0 0 1 17.87 14.54" stroke={c} strokeWidth="1.5" strokeLinecap="round"/><path d="M8.25 6.2L4.05 8.02L7.98 10.77ZM15.75 17.8L19.95 15.98L16.02 13.23Z" fill={c}/></svg>,
  // calendar — used by the reschedule affordance (date·time)
  calendar:(c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="calendar" width={w} height={w} viewBox="0 0 24 24" fill="none"><rect x="3.5" y="5" width="17" height="15.5" rx="3" stroke={c} strokeWidth="1.7"/><path d="M3.5 9.5h17M8 3v3.4M16 3v3.4" stroke={c} strokeWidth="1.7" strokeLinecap="round"/></svg>,
  /* ── the three the leading column had to be given ──────────────────────────
     ROUND TEN (Marwan, 2026-09-01). A row on the Settings root now says what
     it is ABOUT before it says anything else, and the set had nothing for
     three of those subjects. Each is drawn for 17px, which is the size that
     column is, and checked upward — see the sync mark, which was proportioned
     at 240 and vanished at 17.

     THE DRUM IS WHAT IS KEPT. Not the app's `database`, which is a table with
     its columns ruled and means a database somebody made; this is bytes at
     rest. Its two bands are deliberate: they are the two capacity bars drawn
     directly beneath the row it labels, and the mark and the reading under it
     say the same thing. The sides and the band both hang off the ellipse's own
     rx, or the barrel tapers — a drum that tapers is a bucket. */
  drum:   (c='#fff',w=17)=><svg aria-hidden="true" focusable="false" data-md-glyph="drum" width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><ellipse cx="12" cy="6.5" rx="7.5" ry="3"/><path d="M4.5 6.5v11c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3v-11"/><path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3"/></svg>,
  /* THE DISC IS LIGHT AND DARK IN ONE MARK, which is the only honest icon for
     a row whose three answers are Light, Dark and System — a sun would name
     one of them. The filled half stops on the ring's INNER edge (r - w/2)
     rather than on the ring itself: this ink carries alpha, so a fill lying
     over the stroke composites brighter and lights the whole diameter up. The
     same lesson the sync arrows' heads were redrawn for. */
  contrast:(c='#fff',w=17)=><svg aria-hidden="true" focusable="false" data-md-glyph="contrast" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8.2" stroke={c} strokeWidth="1.8"/><path d="M12 4.7a7.3 7.3 0 0 1 0 14.6z" fill={c}/></svg>,
  /* THE GLOBE'S MERIDIAN IS AN ELLIPSE, not a line crossing the ring, so it
     meets the poles as a tangent rather than a crossing. The equator is the
     one crossing left and it is at the centre, where a globe has a hub anyway;
     it stops on the ring's inner edge for the same reason the disc's fill
     does. Nothing here is a flag: this row is the language the app speaks, and
     a language is not a country. */
  globe:  (c='#fff',w=17)=><svg aria-hidden="true" focusable="false" data-md-glyph="globe" width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><circle cx="12" cy="12" r="8.6"/><ellipse cx="12" cy="12" rx="3.7" ry="8.6"/><path d="M5.2 12h13.6"/></svg>,
  /* THE CLOUD — the account's own store, on the root's first storage reading.
     Marwan chose it over a server rack (2026-09-01): the rack is truer to where
     the bytes physically are, but its two status dots vanish at 17px and its
     stacked slabs echo the drum three rows above it.

     ONE CLOSED PATH AND NOTHING ELSE. Every other mark here is two or three
     shapes; a cloud drawn that way would have its bumps crossing each other,
     and this ink carries alpha (0.72), so every crossing composites to 0.92 and
     lights up. One outline can have no crossing at all.

     AND IT IS THE LIGHTEST OF THE NEW MARKS. 1.6 rather than 1.8 — 1.13px at
     17 — because a silhouette this large carries more line than a chevron does
     at the same stroke. Same argument round nine made about the sync circle:
     equal stroke is not equal weight. */
  cloud:  (c='#fff',w=17)=><svg aria-hidden="true" focusable="false" data-md-glyph="cloud" width={w} height={w} viewBox="0 0 24 24" fill="none" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><path d="M17.4 10.6h-1.2A7.4 7.4 0 1 0 9 19.6h8.4a4.5 4.5 0 0 0 0-9z" stroke={c} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  /* THE LAPTOP — this browser's own store, the reading directly beneath the
     cloud. Marwan chose it over a browser window (2026-09-01): "the window
     looks like a cr card", which it does — a rounded rect with one rule across
     the top is the shape a card is drawn in everywhere else.

     The base line stands CLEAR of the screen rather than meeting it. The alpha
     rule again, from the other side: a desk meeting the screen's bottom edge
     would brighten along the whole join. The gap is also what makes it a laptop
     and not a box with a lid. The base is wider than the screen at both ends,
     or it reads as an underline rather than a machine. */
  laptop: (c='#fff',w=17)=><svg aria-hidden="true" focusable="false" data-md-glyph="laptop" width={w} height={w} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width:w, height:w, minWidth:w, minHeight:w, flex:`0 0 ${w}px` }}><rect x="4.6" y="5.4" width="14.8" height="10.4" rx="2"/><path d="M2.6 19h18.8"/></svg>,
  back:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M14.5 5.5L8 12l6.5 6.5" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  close:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M6.5 6.5l11 11M17.5 6.5l-11 11" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  /* Named and fixed-size like the rest of the set since polish 6: it is no
     longer only the compose sheet's "minimize" — it is the bare mark the record
     sheet minimizes with, and it must not be squeezed by the flex row it lands
     in. The ringed version below is a glyph of its own. */
  minus:   (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="minus" width={w} height={w} viewBox="0 0 24 24" fill="none" style={{width:w,height:w,minWidth:w,minHeight:w,flex:`0 0 ${w}px`}}><path d="M5 12h14" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  /* dismiss — the way out of a screen or a window (md-bar.jsx).

     ONE GLYPH (Marwan, 2026-08-24): "the frame and the -, should merge and be
     treated as one whole glyph so the frame weight should be the same as the -
     glyph weight, then follow the grammar of ManDana's Glyphs in terms of size
     adaptations." The first cut drew the ring as a CSS border on the button, so
     it was the size of the TARGET — 32 to 38px, against 19px neighbours — and
     0.75px thick against this set's 1.8. Two marks pretending to be one.

     Here the ring is `clock`'s and `compass`'s ring, r=9 at the house weight, so
     a dismissal sits in a row of icons at the same optical weight as any of
     them and takes `w` to mean what `w` means everywhere else. The minus is a
     short one: 12.5 units would touch the ring, and a dash that reaches its own
     circle reads as a cancelled surface rather than as a door. */
  dismiss: (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="dismiss" width={w} height={w} viewBox="0 0 24 24" fill="none" style={{width:w,height:w,minWidth:w,minHeight:w,flex:`0 0 ${w}px`}}><circle cx="12" cy="12" r="9" stroke={c} strokeWidth="1.8"/><path d="M8.5 12h7" stroke={c} strokeWidth="1.8" strokeLinecap="round"/></svg>,
  edit:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="edit" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M4.5 19.5l4.2-1 9.8-9.8a2.3 2.3 0 00-3.2-3.2l-9.8 9.8-1 4.2zM13.8 7l3.2 3.2" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  /* signOut — a doorway standing open, and the arrow leaving through it. The
     set had no way-out mark at all, which was fine while Sign out was the only
     row on its list wearing no glyph; once every other row carries one, the
     bare row reads as the unfinished one rather than as the quiet one.

     THE ARROW POINTS AT THE DOOR AND STOPS SHORT OF IT. A head touching the
     frame reads as a door closing on somebody; a gap of a unit and a half reads
     as somebody walking out of one, which is what this is. The frame is open on
     the arrow's side for the same reason — three sides of a box, never four. */
  signOut: (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" data-md-glyph="signOut" width={w} height={w} viewBox="0 0 24 24" fill="none"><path d="M14 4.5h4A1.5 1.5 0 0119.5 6v12a1.5 1.5 0 01-1.5 1.5h-4" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M3.5 12h9M9 8.5l3.5 3.5L9 15.5" stroke={c} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  more:    (c='#fff',w=18)=><svg aria-hidden="true" focusable="false" width={w} height={w} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="5" r="1.45" fill={c}/><circle cx="12" cy="12" r="1.45" fill={c}/><circle cx="12" cy="19" r="1.45" fill={c}/></svg>,
};

/* ───────────────────────── THE BLESSING ─────────────────────────── */
/* Said before beginning anything, and there are two beginnings in this app now:
   opening a record to write in, and beginning a project. It was written inside
   the compose sheet's own head and reachable from nowhere else — so the second
   surface would have had to copy it, and this is not a sentence to keep two
   copies of. It lives here, with the other things every surface may borrow.

   The star is amber deliberately: it is the one warm mark in an app of ink and
   domain hues, and it has read that way at the top of every record since the
   first one. `gap`, weight and letter-spacing are the compose sheet's own,
   measured against its 42px head — moving them would move the line the app has
   always opened with. */
const MD_BLESSING_WORDS = 'In the name of Allah';

function MdBlessing({ size = 10.5, glyph = 10, color = ink(0.6) }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
      <span style={{ display: 'inline-flex', opacity: 0.85 }}>{I.star('#F59E0B', glyph)}</span>
      <span style={{ color, fontSize: size, fontWeight: 750, letterSpacing: 1.5,
        textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{MD_BLESSING_WORDS}</span>
      <span style={{ display: 'inline-flex', opacity: 0.85 }}>{I.star('#F59E0B', glyph)}</span>
    </div>);
}

/* ───────────────────────── CONFIRM DIALOG ───────────────────────── */
// A calm, centered glass alert used for destructive confirmations (delete a
// record from the timeline swipe or the compose More menu). Quiet by default —
// the rose is earned only on the single destructive action.
//
// IT IS ASKED ON <body>, AND THAT IS THE WHOLE OF ITS PLACEMENT (Marwan,
// 2026-08-24, deleting a desk). This drew `position:absolute; z-index:120`
// wherever it was mounted — numbers from a time when everything it interrupted
// lived inside the app root. The root is `isolation: isolate`, so a number
// inside it is sorted against its siblings and never against the surfaces on
// <body>: mounted in the day panel, the dialog's 120 was up against the Desks
// screen's 280 in the SAME host, and lost. The question was painted under the
// screen that asked it. `absolute` also centred it on that 420px panel rather
// than on the screen.
//
// The registry has said what this surface is since N-10 — `host: 'body'`,
// `plane: 200` — so the dialog reads its own row instead of carrying a number
// of its own. One asker cannot bury another's question by standing higher in a
// context neither of them shares.
function ConfirmDialog({ light, accent, icon, title, message,
                         confirmLabel='Delete', cancelLabel='Cancel', destructive=true,
                         onConfirm, onCancel }){
  /* md-theme loads before the arbiter, so reach for it at render time. The
     'confirm' surface seats this dialog on the scene (N-9): browser Back
     answers it exactly like Escape — cancel, never the surface beneath. */
  window.useMdDismissLayer(onCancel, true, null, 'confirm');
  /* Same reason, same timing: md-sheet-size loads after this file, the scene
     before it. Both name the one storey — scene-properties.test.js pins them
     equal — so read the row and fall back to the ledger. */
  const plane = window.mdSceneRegistry?.().confirm?.plane ?? window.MD_SHEET_ASK_Z;
  const rose = light ? '#E11D48' : '#F43F5E';
  const danger = destructive ? rose : (accent || rose);
  const press = {
    onPointerDown:e=>{ e.currentTarget.style.transform='scale(0.965)'; },
    onPointerUp:e=>{ e.currentTarget.style.transform='scale(1)'; },
    onPointerLeave:e=>{ e.currentTarget.style.transform='scale(1)'; },
  };
  const overlay = (
    <div data-md-confirm="true" style={{ position:'fixed', inset:0, zIndex:plane, display:'grid', placeItems:'center', padding:26 }}>
      <div onClick={onCancel} aria-hidden="true" style={{ position:'absolute', inset:0,
        background: light ? 'rgba(20,30,55,0.34)' : 'rgba(0,0,0,0.56)',
        backdropFilter:'blur(3px)', WebkitBackdropFilter:'blur(3px)', animation:'mdConfIn .2s ease' }} />
      {/* 312 rather than 298 because WsActions hands the MENU ROW's label to
          the button — "Delete pursuit", not "Delete". Measured at the button's
          own 15.5px/750 the phrase draws 110px, and at 298 the button is 122px
          wide, so after its padding it broke across two lines on staging. The
          extra fourteen give it 129px, and every shorter label in the app is
          unaffected. */}
      <div role="alertdialog" aria-modal="true" style={{ position:'relative', width:'100%', maxWidth:312,
        borderRadius:'var(--md-radius-sheet)', padding:'26px 22px 18px', textAlign:'center',
        background: light ? 'rgba(252,253,255,0.84)' : 'rgba(30,32,40,0.9)',
        backdropFilter:'blur(34px) saturate(150%)', WebkitBackdropFilter:'blur(34px) saturate(150%)',
        border: light ? '0.75px solid rgba(255,255,255,0.9)' : '0.75px solid rgba(255,255,255,0.1)',
        boxShadow: light ? '0 24px 64px rgba(30,42,72,0.28)' : '0 26px 72px rgba(0,0,0,0.62)',
        animation:'mdConfPop .36s cubic-bezier(.32,1.05,.42,1)' }}>
        {icon !== false &&
          <div style={{ width:54, height:54, borderRadius:'50%', margin:'0 auto 15px', display:'grid', placeItems:'center',
            background: mdRgba(danger, light?0.12:0.18), border:`0.75px solid ${mdRgba(danger, light?0.28:0.4)}` }}>
            {(icon || I.trash)(danger, 24)}
          </div>}
        <div style={{ fontSize:18, fontWeight:750, letterSpacing:0.1, color:ink(0.95), lineHeight:1.25 }}>{title}</div>
        {message &&
          <div style={{ fontSize:13.5, fontWeight:450, color:ink(0.55), lineHeight:1.5, marginTop:8,
            textWrap:'pretty' }}>{message}</div>}
        <div style={{ display:'flex', gap:10, marginTop:22 }}>
          <button onClick={onCancel} {...press} style={{ flex:1, height:48, borderRadius:'var(--md-radius-surface)', border:'none', cursor:'pointer',
            fontSize:15.5, fontWeight:700, letterSpacing:0.2, color:ink(0.78), whiteSpace:'nowrap',
            background: light ? 'rgba(20,30,55,0.06)' : 'rgba(255,255,255,0.08)',
            transition:'transform .12s ease' }}>{cancelLabel}</button>
          <button onClick={onConfirm} {...press} style={{ flex:1, height:48, borderRadius:'var(--md-radius-surface)', border:'none', cursor:'pointer',
            fontSize:15.5, fontWeight:750, letterSpacing:0.2, color:'#fff', background:danger, whiteSpace:'nowrap',
            boxShadow:`0 6px 18px ${mdRgba(danger,0.42)}, inset 0 1px 1px rgba(255,255,255,0.35)`,
            transition:'transform .12s ease' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
  return ReactDOM.createPortal(overlay, document.body);
}

/* ───────────────────────── GLASS PRIMITIVES ───────────────────────── */
// Core frosted glass surface. `tint` overrides the white film (e.g. accent rgba).
function Glass({ radius=24, tint, blur=22, alpha=0.12, stroke=0.22, shadow=true, glow=false,
                 style={}, inner, children }) {
  const light = MD_LIGHT;
  // dark: scale each card's white film off the global tweak base so the relative
  // hierarchy between cards survives while the whole set slides lighter — a
  // luminous frosted sheet, not a smoky panel. (0.12 was the old per-card default.)
  const darkFill = alpha * (MD_GLASS.fill / 0.12);
  const surface = tint || (light ? `rgba(255,255,255,${Math.min(0.88, 0.48 + alpha)})` : `rgba(255,255,255,${darkFill})`);
  // heavier frost in dark so the thin, low-opacity fill still keeps text crisp.
  // TWEAKS (glow/halo/shadow/blur) apply to DARK ONLY — the light theme keeps its
  // original, untweakable depth so toggling Tweaks never disturbs the light look.
  const G = MD_GLASS;
  const blurPx = light ? blur : Math.max(blur, G.blur) * (G.blurMul ?? 1);
  const shMul = G.shadow ?? 1;
  const haloA = G.halo ?? 0;
  const glowA = (G.glow ?? 0) + (glow ? 0.08 : 0);   // legacy per-card glow prop adds a touch
  const boxShadow = (light ? [
    // ── original light depth: top sheen + soft navy drop shadow (tweaks ignored) ──
    `inset 0 1px 0.5px rgba(255,255,255,0.96)`,
    shadow ? `0 1px 2px rgba(24,30,42,0.05), 0 8px 24px rgba(24,30,42,0.055)` : '',
    glow ? `0 8px 26px rgba(24,30,42,0.05)` : '',
  ] : [
    // ── dark: tweakable. shadow = downward drop, halo = tight white ring, glow = wide aura ──
    `inset 0 1px 1px rgba(255,255,255,${G.sheen})`,
    shadow && shMul > 0 ? `0 12px 30px rgba(0,0,0,${(0.4 * shMul).toFixed(3)})` : '',
    haloA > 0 ? `0 0 22px rgba(255,255,255,${haloA.toFixed(3)})` : '',
    glowA > 0 ? `0 0 ${Math.round(30 + 50 * Math.min(1, glowA * 3))}px rgba(255,255,255,${glowA.toFixed(3)})` : '',
  ]).filter(Boolean).join(', ');
  return (
    <div style={{
      position:'relative', borderRadius:radius, overflow:'hidden',
      background: surface,
      // Dark sits over a near-flat backdrop, so the frost does no visible work there.
      // Dropping backdrop-filter in dark removes a Chromium backdrop-filter scroll-ghost
      // artifact (the “hovering” copy near the scroll edges) with no visual change.
      // Light keeps the frost — it genuinely blurs the bright backdrop there.
      backdropFilter: light ? `blur(${blurPx}px) saturate(150%)` : 'none',
      WebkitBackdropFilter: light ? `blur(${blurPx}px) saturate(150%)` : 'none',
      border: light ? `0.75px solid rgba(255,255,255,0.95)` : `0.75px solid rgba(255,255,255,${stroke * (MD_GLASS.stroke / 0.22)})`,
      boxShadow,
      ...style,
    }}>
      {/* top-edge specular sheen — light theme only; dark stays a perfectly uniform frosted sheet */}
      {light && <div style={{ position:'absolute', top:0, left:0, right:0, height:'32%',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0) 100%)',
        pointerEvents:'none' }} />}
      {inner}
      <div style={{ position:'relative', zIndex:1 }}>{children}</div>
    </div>
  );
}

/* MD_PRAYERS and MD_SUNRISE were plain arrays; they are today's computed values
   now. Exposed as GETTERS rather than snapshots so the modules that read
   `window.MD_PRAYERS` during render — md-sunarc.jsx does, and its own comment
   anticipated this — pick up a location or method change with no wiring. A
   snapshot taken at module load would have frozen the first location forever. */
Object.defineProperties(window, {
  MD_PRAYERS: { get: () => mdPrayersFor(0), configurable: true },
  MD_SUNRISE: { get: () => mdSunriseFor(0), configurable: true },
});

/* ── THE FROST — one dress for every full-screen surface (N-12) ────────────
   One dark ground, the light frost, one blur. This material was written out
   by hand in seven places — Settings, Crafts, the catalogue, the database
   window, the Tabs surface, the Workshop, the Project window — and seven
   copies of three declarations is how "no visual change" quietly becomes
   seven slightly different materials. One function owns it now.

   The dark ground is translucent on purpose — the twin of the light frost's
   0.78, and the value every dark popover and menu wears. The tabs-navigation
   trial flattened the windows to a near-opaque 0.98 in passing and N-12
   canonicalised what it found; Marwan asked for the frost back (2026-08-28).
   Phone Tabs stays opaque regardless: its backstop plate, not this ground,
   is what makes it so. */
function mdFrostGround(light) {
  return light ? 'rgba(248,249,250,0.78)' : 'rgba(16,18,22,0.76)';
}
function mdFrostDress(light) {
  return {
    background: mdFrostGround(light),
    backdropFilter: 'blur(30px) saturate(125%)',
    WebkitBackdropFilter: 'blur(30px) saturate(125%)',
  };
}

/* This list is the module's public API — the names other modules and the tests
   are allowed to reach for. Anything used only inside this file stays inside it:
   an export nobody imports reads as a contract, and the next person to touch
   mdDateAt or mdCol would weigh callers that do not exist. */
Object.assign(window, {
  mdNowSec, mdNowMin, mdRefreshToday,
  mdPrayersFor, mdSolarFor,
  mdLocation, mdCalc, mdSetLocation, mdSetCalc, mdClearPrayerCache,
  mdDayKey, mdDayItems, mdPrayerWindowEnd,
  mdFmt, mdEnd, mdHM, mdNextPrayer, mdRgba, MDBoundary,
  mdTodayCol, mdDateForOffset, mdHijri, mdHijriParts, mdOffsetForDate, mdMinOf,
  mdHijriAdjust, mdSetHijriAdjust, mdHijriAdjustIsUsers,
  MDI: I, Glass, ConfirmDialog, MdBlessing, MD_BLESSING_WORDS,
  ink, surf, onAcc, mdSetLight, mdIsLight, mdAccentFor, mdSetGlass, mdFrostDress, mdFrostGround,
  mdChromeVoid,
  mdLoadTheme, mdSaveTheme, mdThemeResolve, mdThemeChoice, mdPrefersDark, mdOnSystemTheme,
  mdSetLang, mdLang,
  mdVivid, mdSetVibrancy,
  mdLightGrad, mdDarkHSL, mdBackdropRGB, mdBackdropToken, mdBackdropCSS,
});
